/**
 * Serwis API dla Kolektora Inwentaryzacyjnego
 */

import api from './api';

const collectorService = {
  // ============================================
  // SESJE
  // ============================================

  /**
   * Pobierz listę sesji kolektora
   */
  async getSessions(params = {}) {
    try {
      const response = await api.get('/collector/sessions', { params });
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania sesji:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Utwórz nową sesję kolektora
   */
  async createSession(data) {
    try {
      const response = await api.post('/collector/sessions', data);
      return response.data;
    } catch (error) {
      console.error('Błąd tworzenia sesji:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Pobierz szczegóły sesji z pozycjami
   */
  async getSession(sessionId) {
    try {
      const response = await api.get(`/collector/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania sesji:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Aktualizuj sesję (nazwa, status, notatki)
   */
  async updateSession(sessionId, data) {
    try {
      const response = await api.put(`/collector/sessions/${sessionId}`, data);
      return response.data;
    } catch (error) {
      console.error('Błąd aktualizacji sesji:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Usuń sesję
   */
  async deleteSession(sessionId) {
    try {
      const response = await api.delete(`/collector/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Błąd usuwania sesji:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Wstrzymaj sesję
   */
  async pauseSession(sessionId) {
    return this.updateSession(sessionId, { status: 'paused' });
  },

  /**
   * Wznów sesję
   */
  async resumeSession(sessionId) {
    return this.updateSession(sessionId, { status: 'in_progress' });
  },

  /**
   * Zakończ sesję (gotowa do konwersji)
   */
  async completeSession(sessionId) {
    return this.updateSession(sessionId, { status: 'completed' });
  },

  // ============================================
  // SKANOWANIE I POZYCJE
  // ============================================

  /**
   * Skanuj kod EAN i dodaj/aktualizuj pozycję
   */
  async scan(sessionId, ean, quantity = 1, productId = null) {
    try {
      const response = await api.post(`/collector/sessions/${sessionId}/scan`, {
        ean,
        quantity,
        product_id: productId
      });
      return response.data;
    } catch (error) {
      console.error('Błąd skanowania:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Aktualizuj ilość pozycji
   */
  async updateItem(sessionId, itemId, data) {
    try {
      const response = await api.put(
        `/collector/sessions/${sessionId}/items/${itemId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Błąd aktualizacji pozycji:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Usuń pozycję
   */
  async deleteItem(sessionId, itemId) {
    try {
      const response = await api.delete(
        `/collector/sessions/${sessionId}/items/${itemId}`
      );
      return response.data;
    } catch (error) {
      console.error('Błąd usuwania pozycji:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // ============================================
  // KONWERSJA DO INWENTURY
  // ============================================

  /**
   * Konwertuj sesję kolektora do pełnej inwentury
   */
  async convertToInventory(sessionId) {
    try {
      const response = await api.post(`/collector/sessions/${sessionId}/convert`);
      return response.data;
    } catch (error) {
      console.error('Błąd konwersji:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // ============================================
  // OFFLINE SUPPORT (localStorage)
  // ============================================

  /**
   * Pobierz kolejkę offline
   */
  getOfflineQueue() {
    try {
      return JSON.parse(localStorage.getItem('collector_offline_queue') || '[]');
    } catch {
      return [];
    }
  },

  /**
   * Dodaj operację do kolejki offline
   */
  addToOfflineQueue(action) {
    const queue = this.getOfflineQueue();
    queue.push({
      ...action,
      timestamp: Date.now(),
      id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    });
    localStorage.setItem('collector_offline_queue', JSON.stringify(queue));
    return queue.length;
  },

  /**
   * Wyczyść kolejkę offline
   */
  clearOfflineQueue() {
    localStorage.removeItem('collector_offline_queue');
  },

  /**
   * Synchronizuj kolejkę offline z serwerem
   */
  async syncOfflineQueue(sessionId) {
    const queue = this.getOfflineQueue();
    if (queue.length === 0) {
      return { success: true, synced: 0, failed: 0 };
    }

    let synced = 0;
    let failed = 0;
    const failedItems = [];

    for (const action of queue) {
      try {
        if (action.type === 'scan') {
          const result = await this.scan(
            action.sessionId || sessionId,
            action.ean,
            action.quantity || 1,
            action.productId ?? null
          );
          if (result.success) {
            synced++;
          } else {
            failed++;
            failedItems.push(action);
          }
        }
      } catch (error) {
        failed++;
        failedItems.push(action);
      }
    }

    // Zapisz tylko nieudane operacje
    if (failedItems.length > 0) {
      localStorage.setItem('collector_offline_queue', JSON.stringify(failedItems));
    } else {
      this.clearOfflineQueue();
    }

    return {
      success: true,
      synced,
      failed,
      remaining: failedItems.length
    };
  },

  // ============================================
  // CACHE PRODUKTÓW (dla offline)
  // ============================================

  /**
   * Zapisz produkty w cache lokalnym
   */
  cacheProducts(products) {
    try {
      localStorage.setItem('collector_products_cache', JSON.stringify({
        products,
        timestamp: Date.now()
      }));
    } catch (error) {
      // cache save failed
    }
  },

  /**
   * Pobierz produkty z cache (max 24h)
   */
  getCachedProducts() {
    try {
      const cached = JSON.parse(localStorage.getItem('collector_products_cache') || 'null');
      if (cached && (Date.now() - cached.timestamp) < 24 * 60 * 60 * 1000) {
        return cached.products;
      }
    } catch {
      // Ignore
    }
    return null;
  },

  /**
   * Wyszukaj produkt w cache po EAN
   */
  findProductInCache(ean) {
    const products = this.getCachedProducts();
    if (!products) return null;
    return products.find(p => p.ean === ean || p.primary_ean === ean);
  },

  // ============================================
  // WYSZUKIWANIE PRODUKTÓW
  // ============================================

  /**
   * Wyszukaj produkty po nazwie/kodzie/EAN
   */
  async searchProducts(query, locationId, limit = 15) {
    try {
      const response = await api.get('/products/search', {
        params: {
          query,
          location_id: locationId,
          limit
        }
      });
      return response.data;
    } catch (error) {
      console.error('Błąd wyszukiwania produktów:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  /**
   * Pobierz produkt po kodzie EAN
   */
  async getProductByEan(ean, locationId) {
    try {
      const response = await api.get(`/ean/${ean}`, {
        params: { location_id: locationId }
      });

      // API zwraca: { success, found_in, product: { product_id, ean, nazwa, stock, ... } }
      if (response.data && response.data.success && response.data.product) {
        const p = response.data.product;
        // Przekaz PELNY komplet pol z API (nie tylko podzbior),
        // zeby konsument mial cene sprzedazy, VAT i stan do wyliczenia marzy.
        // Brakujace wartosci pozostawiamy jako null (nie udajemy 0) -> falszywa marza.
        const hasStock = p.stock !== undefined && p.stock !== null;
        return {
          success: true,
          data: {
            ...p,
            id: p.product_id,
            ean: p.ean,
            name: p.nazwa,
            nazwa: p.nazwa,
            stawka_vat: p.stawka_vat ?? null,
            cena: p.cena ?? p.cena_sprzedazy_brutto ?? null,
            cena_sprzedazy_netto: p.cena_sprzedazy_netto ?? null,
            cena_sprzedazy_brutto: p.cena_sprzedazy_brutto ?? null,
            // Cena zakupu nie jest zwracana przez /ean -> null (nieznana), nie 0
            cena_zakupu: p.cena_zakupu ?? p.current_purchase_price ?? null,
            current_purchase_price: p.current_purchase_price ?? p.cena_zakupu ?? null,
            // Stan: null gdy nieznany (brak location_id lub brak rekordu), nie falszywe 0
            stock: hasStock ? p.stock : null,
            stan: hasStock ? p.stock : null
          }
        };
      }

      return { success: false, error: 'Nie znaleziono produktu' };
    } catch (error) {
      console.error('Błąd pobierania produktu:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }
};

export default collectorService;
